import React, { useState } from "react";
import { useForm, Controller } from "react-hook-form";
import { IMEICheckFailureDialog } from "./imei-check-failure-dialog";
import { IMEICheckSuccessDialog } from "./imei-check-success-dialog";
import { useGlobalSlice } from "app/slice";
import {
  useStaggeredAnimation,
  generateAnimationDelays,
} from "utils/hooks/use-staggered-animation";
import { useImeiCheckRateLimit } from "../hooks/use-imei-check-rate-limit";
import { formatCooldownMinutesSeconds } from "../utils/imei-check-attempt-storage";

import { Label } from "app/components/ui/label";
import { Input } from "app/components/ui/input";
import { Button } from "app/components/ui/button";
import { Icons } from "app/components/ui/icons";

const ANIMATION_DELAYS = generateAnimationDelays(4);

export const BringYourPhone = () => {
  const [showIMEICheckFailureDialog, setShowIMEICheckFailureDialog] =
    useState(false);
  const [showIMEICheckSuccessDialog, setShowIMEICheckSuccessDialog] =
    useState(false);

  const form = useForm({
    defaultValues: {
      imei: "",
    },
    mode: "onChange",
  });

  const {
    control,
    handleSubmit,
    watch,
    formState: { errors },
  } = form;

  const imeiValue = watch("imei");
  const { inCooldown, isBlocked, remainingSeconds, registerAttempt } =
    useImeiCheckRateLimit();

  const { sectionRef, animatedStates } = useStaggeredAnimation({
    delays: ANIMATION_DELAYS,
    threshold: 0.1,
  });

  const [h1Animated, pAnimated, formAnimated, helpTextAnimated] =
    animatedStates;

  // Get validation mutation from global slice
  const { useValidateDeviceMutation } = useGlobalSlice();
  const [validateDevice, { isLoading }] = useValidateDeviceMutation();

  const handleHowToFindYourIMEI = () => {
    const element = document.getElementById("how-to-find-your-imei");
    if (element) {
      const elementRect = element.getBoundingClientRect();
      const elementPosition = elementRect.top + window.scrollY;
      const offsetPosition = elementPosition - 110;

      window.scrollTo({
        top: offsetPosition,
        behavior: "smooth",
      });
    }
  };

  const onSubmit = async (data: { imei: string }) => {
    if (!data.imei || data.imei.length !== 15 || inCooldown) return;
    try {
      const response = await validateDevice({ imei: data.imei }).unwrap();
      if (response?.data?.success === true) {
        setShowIMEICheckSuccessDialog(true);
      } else {
        setShowIMEICheckFailureDialog(true);
      }
    } catch {
      setShowIMEICheckFailureDialog(true);
    } finally {
      registerAttempt();
    }
  };

  const handleKeyDown = (e: React.KeyboardEvent<HTMLInputElement>) => {
    if (e.ctrlKey || e.metaKey) return;
    if (
      [
        "Backspace",
        "Delete",
        "Tab",
        "ArrowLeft",
        "ArrowRight",
        "Home",
        "End",
      ].includes(e.key)
    )
      return;
    if (!/\d/.test(e.key)) e.preventDefault();
  };

  return (
    <React.Fragment>
      <section
        ref={sectionRef}
        className="px-5 pt-8 sm:px-6 md:px-7 lg:px-9 xl:px-12 sm:pt-10 md:pt-[3.25rem] lg:pt-[4.06rem] pb-9 sm:pb-14 md:pb-[5.5rem] lg:pb-[7.5rem]"
      >
        <div className="text-center mb-6 sm:mb-8 md:mb-10 lg:mb-[3.125rem]">
          <h1
            className={`font-mobile text-[#1D1D1B] text-[clamp(1.75rem,calc(3.25vw+1rem),4rem)] font-normal leading-tight md:leading-[1.3] lg:leading-[5.25rem] mb-0.5 transition-all duration-1000 ease-out ${
              h1Animated
                ? "opacity-100 translate-y-0"
                : "opacity-0 translate-y-10"
            }`}
          >
            Bring Your Phone to VLE Mobile!
          </h1>
          <p
            className={`max-w-[66.625rem] text-[#1D1D1B] text-sm sm:text-base md:text-[1.15rem] lg:text-[1.22069rem] font-normal leading-5 sm:leading-[1.375rem] md:leading-6 lg:leading-7 tracking-[-0.0375rem] mx-auto transition-all duration-1000 ease-out ${
              pAnimated
                ? "opacity-100 translate-y-0"
                : "opacity-0 translate-y-10"
            }`}
          >
            Good news! Most unlocked phones work great with VLE Mobile. Let's
            check if your phone is ready for triple network access and amazing
            security features.
          </p>
        </div>
        <form
          className={`w-full max-w-[39.75rem] bg-white p-4 sm:p-5 md:p-6 lg:p-[1.625rem] border border-[#E5E7EB] rounded-xl md:rounded-2xl mx-auto overflow-hidden transition-all duration-1000 ease-out ${
            formAnimated
              ? "opacity-100 translate-y-0"
              : "opacity-0 translate-y-10"
          }`}
          onSubmit={handleSubmit(onSubmit)}
        >
          <h3 className="font-mobile text-black text-base sm:text-lg md:text-xl lg:text-2xl font-bold leading-[1.125rem] sm:leading-5 md:leading-6 lg:leading-7 mb-2.5 sm:mb-3 lg:mb-[0.94rem]">
            Let's Check Your Phone!
          </h3>
          <div className="mb-2.5 sm:mb-3">
            <Label
              htmlFor="imei"
              className="w-fit text-black text-xs sm:text-[0.825rem] md:text-sm font-medium leading-4 md:leading-5 mb-1 md:mb-[0.375rem]"
            >
              Enter your phone's IMEI number:
            </Label>
            <Controller
              name="imei"
              control={control}
              rules={{
                required: "IMEI number is required!",
                validate: {
                  exactLength: (value) => {
                    if (!value) return true;
                    const imei = value.replace(/\D/g, "");
                    if (imei.length !== 15) {
                      return "IMEI number must be exactly 15 digits";
                    }
                    return true;
                  },
                },
              }}
              render={({ field }) => (
                <div className="relative">
                  <Input
                    id="imei"
                    type="tel"
                    inputMode="numeric"
                    autoComplete="imei"
                    value={field.value || ""}
                    onChange={(e) => {
                      const numbers = e.target.value.replace(/\D/g, "");
                      const finalValue = numbers.slice(0, 15);

                      field.onChange(finalValue);

                      if (numbers.length === 15) {
                        form.clearErrors("imei");
                      } else {
                        setTimeout(() => form.trigger("imei"), 0);
                      }
                    }}
                    onKeyDown={handleKeyDown}
                    className={`bg-white h-10 md:h-[2.875rem] p-2 md:p-[0.78125rem] md:pb-[0.84375rem] rounded-xl text-black placeholder:text-black/20 text-[0.825rem] sm:text-sm font-medium leading-4 md:leading-5 focus-visible:outline focus:outline-[#707C8F] ${
                      errors.imei
                        ? "border-[#D80027] focus:outline-[#D80027]"
                        : "border-[#D1D5DB] focus:outline-[#707C8F]"
                    }`}
                    maxLength={15}
                    aria-label="IMEI Code"
                    placeholder="Type your 15 digit IMEI here"
                    disabled={isBlocked}
                  />
                </div>
              )}
            />
            {errors.imei && (
              <p className="text-[#D80027] text-[0.72rem] sm:text-xs font-normal leading-5 mt-0.5 sm:mt-1">
                <span className="font-bold">*</span>
                {errors.imei?.message as string}
              </p>
            )}
          </div>
          {inCooldown && (
            <p
              className="text-[0.825rem] sm:text-sm font-medium leading-[1.125rem] sm:leading-5 mb-2.5 sm:mb-3 text-[#991B1B]"
              role="status"
              aria-live="polite"
            >
              Please wait{" "}
              <span className="tabular-nums font-semibold">
                {formatCooldownMinutesSeconds(remainingSeconds)}
              </span>{" "}
              before trying again.
            </p>
          )}
          <Button
            type="submit"
            variant="primary"
            disabled={isLoading || imeiValue?.length !== 15 || isBlocked}
          >
            {isLoading ? (
              <div className="flex items-center gap-0.5 sm:gap-1">
                <Icons.Spinner className="w-4 h-4 animate-spin md:w-5 md:h-5" />
                <span>Checking...</span>
              </div>
            ) : (
              "Check My Phone"
            )}
          </Button>
          <p
            className={`text-center text-black [@media(max-width:400px)]:text-[0.7rem] text-[0.72rem] sm:text-xs lg:text-[0.8125rem] font-semibold leading-[0.95rem] sm:leading-4 lg:leading-[1.0725rem] mt-3 sm:mt-3.5 lg:mt-4 transition-all duration-1000 ease-out ${
              helpTextAnimated
                ? "opacity-100 translate-y-0"
                : "opacity-0 translate-y-10"
            }`}
          >
            Don't know your IMEI?{" "}
            <span
              className="text-[#F45F37] hover:text-[#E44501] hover:underline cursor-pointer"
              onClick={handleHowToFindYourIMEI}
              role="button"
              tabIndex={0}
              onKeyDown={(e) => {
                if (e.key === "Enter" || e.key === " ") {
                  e.preventDefault();
                  handleHowToFindYourIMEI();
                }
              }}
              aria-label="How to find your IMEI"
            >
              We'll show you how to find it!
            </span>
          </p>
        </form>
      </section>
      <IMEICheckFailureDialog
        showDialog={showIMEICheckFailureDialog}
        setShowDialog={setShowIMEICheckFailureDialog}
      />
      <IMEICheckSuccessDialog
        showDialog={showIMEICheckSuccessDialog}
        setShowDialog={setShowIMEICheckSuccessDialog}
      />
    </React.Fragment>
  );
};
